import React, { useState, useEffect, useMemo } from 'react';

// =====================================================
// INTERFACES
// =====================================================

interface MiniCalendarSlideProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  appointmentDates: string[]; // Fechas que tienen turnos (formato YYYY-MM-DD)
  workingDays: number[]; // Días de la semana que trabaja (0=Dom, 1=Lun, etc.)
  blockedDates?: string[]; // Fechas con horarios bloqueados (formato YYYY-MM-DD)
  inline?: boolean; // Si es true, renderiza como componente estático (sidebar desktop)
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const MiniCalendarSlide: React.FC<MiniCalendarSlideProps> = ({
  isOpen,
  onClose,
  selectedDate,
  onDateSelect,
  appointmentDates,
  workingDays,
  blockedDates = [],
  inline = false
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate));
  const [isAnimating, setIsAnimating] = useState(false);

  // Sincronizar mes cuando cambia la fecha seleccionada
  useEffect(() => {
    setCurrentMonth(new Date(selectedDate));
  }, [selectedDate]);

  // Manejar animación de entrada/salida
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  // Nombres de días y meses
  const dayNames = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Generar días del calendario
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // Primer día del mes
    const firstDay = new Date(year, month, 1);
    // Último día del mes
    const lastDay = new Date(year, month + 1, 0);
    
    // Ajustar para que la semana empiece en Lunes
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    
    const days: Array<{
      date: Date;
      day: number;
      isCurrentMonth: boolean;
      isToday: boolean;
      isSelected: boolean;
      hasAppointments: boolean;
      isWorkingDay: boolean;
      isBlocked: boolean;
    }> = [];

    // Días del mes anterior
    const prevMonth = new Date(year, month, 0);
    for (let i = startDay - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonth.getDate() - i);
      days.push({
        date,
        day: date.getDate(),
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
        hasAppointments: false,
        isWorkingDay: false,
        isBlocked: false
      });
    }

    // Días del mes actual
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      const dateStr = date.toISOString().split('T')[0];
      const dayOfWeek = date.getDay();
      
      days.push({
        date,
        day,
        isCurrentMonth: true,
        isToday: date.getTime() === today.getTime(),
        isSelected: date.toISOString().split('T')[0] === selectedDate.toISOString().split('T')[0],
        hasAppointments: appointmentDates.includes(dateStr),
        isWorkingDay: workingDays.includes(dayOfWeek),
        isBlocked: blockedDates.includes(dateStr)
      });
    }

    // Días del mes siguiente (completar hasta 42 para 6 filas)
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push({
        date,
        day,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
        hasAppointments: false,
        isWorkingDay: false,
        isBlocked: false
      });
    }

    return days;
  }, [currentMonth, selectedDate, appointmentDates, workingDays, blockedDates]);

  // Navegación de meses
  const goToPrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    onDateSelect(today);
  };

  // Manejar click en día
  const handleDayClick = (dayInfo: typeof calendarDays[0]) => {
    if (dayInfo.isCurrentMonth) {
      onDateSelect(dayInfo.date);
    }
  };

  // Click fuera para cerrar
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!inline && !isOpen && !isAnimating) return null;

  // Contenido del calendario reutilizable
  const calendarContent = (
    <>
      {/* Header */}
      <div className={`flex-shrink-0 ${inline ? 'pb-3' : 'px-4 py-4 border-b border-gray-100'}`}>
        <div className="flex items-center justify-between mb-3">
          <h2 className={`${inline ? 'text-sm' : 'text-lg'} font-bold text-gray-900`}>
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h2>
          <div className="flex items-center space-x-0.5">
            <button
              onClick={goToPrevMonth}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className={`${inline ? 'w-4 h-4' : 'w-5 h-5'} text-gray-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={goToNextMonth}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className={`${inline ? 'w-4 h-4' : 'w-5 h-5'} text-gray-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Días de la semana */}
        <div className="grid grid-cols-7 gap-1">
          {dayNames.map((day, index) => (
            <div
              key={index}
              className="text-center text-[11px] font-semibold text-gray-400 uppercase py-1"
            >
              {day}
            </div>
          ))}
        </div>
      </div>

      {/* Grid de días */}
      <div className={inline ? 'py-1' : 'flex-1 px-4 py-2 overflow-y-auto'}>
        <div className="grid grid-cols-7 gap-0.5">
          {calendarDays.map((dayInfo, index) => {
            const isClickable = dayInfo.isCurrentMonth;

            return (
              <button
                key={index}
                onClick={() => isClickable && handleDayClick(dayInfo)}
                disabled={!isClickable}
                title={dayInfo.isBlocked ? 'Tiene horarios bloqueados' : undefined}
                className={`
                  relative ${inline ? 'w-8 h-8 text-xs' : 'w-9 h-9 text-sm'} rounded-full flex items-center justify-center
                  transition-all duration-150 ease-out
                  ${!dayInfo.isCurrentMonth ? 'text-gray-300 cursor-default' : ''}
                  ${dayInfo.isCurrentMonth && !dayInfo.isSelected && !dayInfo.isToday && !dayInfo.hasAppointments ? 'text-gray-700 hover:bg-gray-100' : ''}
                  ${dayInfo.isToday && !dayInfo.isSelected ? 'font-bold text-blue-600 ring-1 ring-blue-300' : ''}
                  ${dayInfo.isSelected ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-semibold shadow-md shadow-blue-500/25' : ''}
                  ${dayInfo.hasAppointments && !dayInfo.isSelected ? 'font-semibold text-emerald-700' : ''}
                  ${dayInfo.isBlocked && !dayInfo.isSelected ? 'ring-1 ring-red-300 bg-red-50/60' : ''}
                `}
              >
                {dayInfo.day}

                {/* Indicadores en la base del día */}
                {!dayInfo.isSelected && (dayInfo.hasAppointments || dayInfo.isBlocked) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 flex items-center gap-0.5">
                    {dayInfo.hasAppointments && (
                      <span className="w-1 h-1 rounded-full bg-emerald-500" />
                    )}
                    {dayInfo.isBlocked && (
                      <span className="w-1 h-1 rounded-full bg-red-500" />
                    )}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className={`flex-shrink-0 ${inline ? 'pt-3 mt-2 border-t border-gray-100' : 'px-4 py-4 border-t border-gray-100'} space-y-2`}>
        <button
          onClick={goToToday}
          className={`w-full ${inline ? 'py-2 text-sm' : 'py-2.5'} px-4 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 hover:border-gray-300 transition-all`}
        >
          Hoy
        </button>

        <div className="flex items-center justify-center flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-400">
          <div className="flex items-center space-x-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            <span>Con turnos</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600"></span>
            <span>Seleccionado</span>
          </div>
          {blockedDates.length > 0 && (
            <div className="flex items-center space-x-1">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
              <span>Bloqueado</span>
            </div>
          )}
        </div>
      </div>
    </>
  );

  // Modo inline: renderizar como componente estático en sidebar
  if (inline) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3">
        {calendarContent}
      </div>
    );
  }

  // Modo slide: overlay con animación
  return (
    <>
      {/* Backdrop */}
      <div
        className={`
          fixed inset-0 bg-black/20 backdrop-blur-sm z-[200]
          transition-opacity duration-300 ease-out
          ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
        onClick={handleBackdropClick}
      />

      {/* Panel del calendario */}
      <div
        className={`
          fixed top-0 left-0 h-full w-80 max-w-[85vw]
          bg-white shadow-2xl z-[201]
          transform transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        onTransitionEnd={() => {
          if (!isOpen) setIsAnimating(false);
        }}
      >
        <div className="h-full flex flex-col px-4 py-4">
          {calendarContent}

          {/* Botón cerrar (móvil) */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
};

export default MiniCalendarSlide;